import { useState, useEffect, useRef } from 'react';
import { TutorialManager } from './TutorialManager';
import { PostItNote } from './PostItNote';
import { AudioEngine, ButtonColor } from '../core/AudioEngine';
import './TutorialGame.css';

interface TutorialGameProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface Button {
  id: number;
  x: number;
  y: number;
  color: string;
  audioColor: ButtonColor;
}

const BUTTON_COLORS = ['#FF6B6B', '#4ECDC4', '#FFD93D', '#A78BFA'];
const BUTTON_AUDIO_COLORS: ButtonColor[] = ['red', 'green', 'yellow', 'blue'];

export const TutorialGame = ({ onComplete, onSkip }: TutorialGameProps) => {
  const [tutorialManager] = useState(() => new TutorialManager());
  const [buttons, setButtons] = useState<Button[]>([]);
  const [highlightedButton, setHighlightedButton] = useState<number | null>(null);
  const [blinkingButton, setBlinkingButton] = useState<number | null>(null);
  const [isPlayingSequence, setIsPlayingSequence] = useState(false);
  const [audioEngine] = useState(() => new AudioEngine());
  const [postItMessage, setPostItMessage] = useState<string>('');
  const [showPostItNext, setShowPostItNext] = useState(false);
  const [audioOnlyMode, setAudioOnlyMode] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Initialize audio and buttons
  useEffect(() => {
    audioEngine.init();
    initButtons();
    tutorialManager.startStep('TUTORIAL_OBSERVE');
    setPostItMessage('Observa la secuencia que Simon muestra');
    setShowPostItNext(false);
  }, []);

  // Play sequence when tutorial step changes
  useEffect(() => {
    const step = tutorialManager.getCurrentStep();
    if (step && step.sequence.length > 0) {
      setTimeout(() => {
        playSequence();
      }, 500);
    }
  }, [tutorialManager.getCurrentState()]);

  const initButtons = () => {
    const newButtons: Button[] = [];
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    for (let i = 0; i < 4; i++) {
      newButtons.push({
        id: i,
        x: positions[i].x,
        y: positions[i].y,
        color: BUTTON_COLORS[i],
        audioColor: BUTTON_AUDIO_COLORS[i],
      });
    }

    setButtons(newButtons);
  };

  const reorganizeButtons = () => {
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    // Shuffle positions
    const shuffled = [...positions].sort(() => Math.random() - 0.5);

    setButtons((prev) =>
      prev.map((btn, i) => ({
        ...btn,
        x: shuffled[i].x,
        y: shuffled[i].y,
      }))
    );
  };

  const playSequence = async () => {
    setIsPlayingSequence(true);
    const step = tutorialManager.getCurrentStep();
    if (!step) return;

    const audioOnlyMode = tutorialManager.getAudioOnlyMode();

    // Stop any blinking during sequence playback
    setBlinkingButton(null);

    for (const buttonIndex of step.sequence) {
      const button = buttons[buttonIndex];
      if (!button) continue;

      // Highlight button (unless audio-only mode)
      if (!audioOnlyMode) {
        setHighlightedButton(buttonIndex);
      }

      // Play sound with position
      audioEngine.playTone(button.audioColor, button.x, 600, 600);

      await new Promise((resolve) => setTimeout(resolve, 800));

      setHighlightedButton(null);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    setIsPlayingSequence(false);
    
    // Start blinking the first button in the sequence after playback
    tutorialManager.startBlinking();
    setBlinkingButton(tutorialManager.getBlinkTarget());

    // Update post-it message after sequence for TUTORIAL_OBSERVE
    const currentState = tutorialManager.getCurrentState();
    if (currentState === 'TUTORIAL_OBSERVE') {
      setPostItMessage('¡Ahora toca los botones en orden! El que brilla es el siguiente 👉');
      // Automatically transition to TUTORIAL_REPEAT
      setTimeout(() => {
        tutorialManager.advance();
      }, 500);
    }
  };

  const handleButtonClick = (buttonIndex: number) => {
    const step = tutorialManager.getCurrentStep();
    if (!step || !step.allowInput || isPlayingSequence) return;

    const button = buttons[buttonIndex];
    if (!button) return;

    // Play sound
    audioEngine.playTone(button.audioColor, button.x, 600, 400);

    // Visual feedback
    setHighlightedButton(buttonIndex);
    setTimeout(() => setHighlightedButton(null), 200);

    // Record input
    const result = tutorialManager.recordInput(buttonIndex);

    // Update blinking based on result
    setBlinkingButton(tutorialManager.getBlinkTarget());

    const currentState = tutorialManager.getCurrentState();

    if (result === 'incorrect') {
      // Show error in PostItNote
      if (currentState === 'TUTORIAL_REPEAT') {
        setPostItMessage('¡Ups! Inténtalo de nuevo');
        setShowPostItNext(false);
      } else {
        // Show error feedback for other states
        if ((window as any)._tutorialShowFeedback) {
          (window as any)._tutorialShowFeedback('incorrect');
        }
      }
      
      // Stop blinking temporarily
      setBlinkingButton(null);
      
      // Replay sequence
      setTimeout(() => {
        playSequence();
      }, 1500);
    } else if (result === 'complete') {
      // Stop blinking on completion
      setBlinkingButton(null);
      
      if (currentState === 'TUTORIAL_REPEAT') {
        // Show celebration in PostItNote
        setPostItMessage('¡Perfecto! 🎉');
        setShowPostItNext(true);
      } else {
        // Show success for other states
        if ((window as any)._tutorialShowSuccess) {
          (window as any)._tutorialShowSuccess();
        }
      }
    } else {
      // Correct but not complete - blink updates to next button
      if (currentState === 'TUTORIAL_REPEAT') {
        // Keep instructional message
        setPostItMessage('Toca los botones en el mismo orden. ¡El que brilla es el siguiente!');
      } else {
        if ((window as any)._tutorialShowFeedback) {
          (window as any)._tutorialShowFeedback('correct');
        }
      }
    }
  };

  const handleContinue = () => {
    const currentState = tutorialManager.getCurrentState();

    if (currentState === 'TUTORIAL_REPEAT') {
      setPostItMessage('En Simon Revolutions, los botones cambian de posición entre rondas. ¡Fíjate bien!');
      setShowPostItNext(false);
      
      setTimeout(() => {
        reorganizeButtons();
      }, 500);
      
      setTimeout(() => {
        tutorialManager.advance();
        playSequence();
      }, 1500);
    } else if (currentState === 'TUTORIAL_ORGANIC') {
      setPostItMessage('Cierra los ojos y escucha. El audio te indica dónde está cada botón.');
      setShowPostItNext(false);
      
      setTimeout(() => {
        tutorialManager.advance();
        playSequence();
      }, 500);
    } else if (currentState === 'TUTORIAL_AUDIO') {
      tutorialManager.advance();
      setShowPostItNext(false);
    } else if (currentState === 'TUTORIAL_COMPLETE') {
      onComplete();
    } else {
      const nextState = tutorialManager.advance();
      if (!nextState) {
        onComplete();
      } else {
        setShowPostItNext(false);
      }
    }
  };

  const handleToggleAudioOnly = () => {
    const newMode = !audioOnlyMode;
    setAudioOnlyMode(newMode);
    tutorialManager.setAudioOnlyMode(newMode);
    playSequence();
  };

  const renderPostItContent = () => {
    const currentState = tutorialManager.getCurrentState();

    if (currentState === 'TUTORIAL_AUDIO') {
      return (
        <div>
          <p>{postItMessage}</p>
          <div style={{ marginTop: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={audioOnlyMode}
                onChange={handleToggleAudioOnly}
              />
              <span style={{ fontSize: '14px' }}>Solo audio (sin iluminar botones)</span>
            </label>
          </div>
        </div>
      );
    }

    if (currentState === 'TUTORIAL_COMPLETE') {
      return (
        <div>
          <p style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '12px' }}>
            ¡Ya estás listo para jugar!
          </p>
          <ol style={{ textAlign: 'left', paddingLeft: '20px', margin: '8px 0' }}>
            <li>Memoriza la secuencia</li>
            <li>Los botones cambian de lugar</li>
            <li>Usa el audio como guía</li>
          </ol>
        </div>
      );
    }

    return postItMessage;
  };


  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw buttons
    buttons.forEach((button) => {
      ctx.save();
      
      ctx.beginPath();
      ctx.arc(button.x, button.y, 60, 0, 2 * Math.PI);
      
      if (highlightedButton === button.id) {
        ctx.fillStyle = button.color;
        ctx.shadowBlur = 20;
        ctx.shadowColor = button.color;
      } else {
        ctx.fillStyle = button.color;
        ctx.globalAlpha = 0.7;
        ctx.shadowBlur = 0;
      }
      
      ctx.fill();
      
      // Apply blink glow effect
      if (blinkingButton === button.id && !isPlayingSequence) {
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 4;
        ctx.shadowBlur = 12;
        ctx.shadowColor = '#FFD700';
        ctx.stroke();
      }
      
      ctx.restore();
    });
  }, [buttons, highlightedButton, blinkingButton, isPlayingSequence]);

  // Blink animation loop
  useEffect(() => {
    if (blinkingButton === null || isPlayingSequence) return;

    let animationFrame: number;
    let startTime = Date.now();

    const animate = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const elapsed = Date.now() - startTime;
      const cycle = (elapsed % 1000) / 1000; // 1 second cycle (0.5s specified, but 1s feels smoother)
      const intensity = Math.sin(cycle * Math.PI * 2) * 0.5 + 0.5;

      // Redraw canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      buttons.forEach((button) => {
        ctx.save();
        
        ctx.beginPath();
        ctx.arc(button.x, button.y, 60, 0, 2 * Math.PI);
        
        if (highlightedButton === button.id) {
          ctx.fillStyle = button.color;
          ctx.shadowBlur = 20;
          ctx.shadowColor = button.color;
        } else {
          ctx.fillStyle = button.color;
          ctx.globalAlpha = 0.7;
          ctx.shadowBlur = 0;
        }
        
        ctx.fill();
        
        // Apply blink glow effect with animation
        if (blinkingButton === button.id) {
          ctx.strokeStyle = '#FFD700';
          ctx.lineWidth = 4;
          ctx.shadowBlur = 8 + intensity * 8;
          ctx.shadowColor = '#FFD700';
          ctx.globalAlpha = 0.8 + intensity * 0.2;
          ctx.stroke();
        }
        
        ctx.restore();
      });

      animationFrame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [blinkingButton, buttons, highlightedButton, isPlayingSequence]);

  return (
    <div className="tutorial-game">
      <canvas
        ref={canvasRef}
        width={600}
        height={500}
        className="tutorial-canvas"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          // Check which button was clicked
          buttons.forEach((button) => {
            const dx = x - button.x;
            const dy = y - button.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= 60) {
              handleButtonClick(button.id);
            }
          });
        }}
      />

      {/* Use PostItNote for all tutorial states */}
      {(tutorialManager.getCurrentState() === 'TUTORIAL_OBSERVE' || 
        tutorialManager.getCurrentState() === 'TUTORIAL_REPEAT' ||
        tutorialManager.getCurrentState() === 'TUTORIAL_ORGANIC' ||
        tutorialManager.getCurrentState() === 'TUTORIAL_AUDIO' ||
        tutorialManager.getCurrentState() === 'TUTORIAL_COMPLETE') && (
        <PostItNote
          message={renderPostItContent()}
          position={tutorialManager.getCurrentState() === 'TUTORIAL_COMPLETE' ? 'center' : 'top-center'}
          pointerTo={tutorialManager.getCurrentState() === 'TUTORIAL_COMPLETE' ? null : 'button-area'}
          onNext={showPostItNext || tutorialManager.getCurrentState() === 'TUTORIAL_COMPLETE' ? handleContinue : undefined}
          onSkip={onSkip}
          showSkipButton={true}
          showNextButton={showPostItNext || tutorialManager.getCurrentState() === 'TUTORIAL_COMPLETE'}
        />
      )}

      <button 
        className="skip-button"
        onClick={onSkip}
      >
        Saltar Tutorial
      </button>
    </div>
  );
};
